/**
 * AD-35: one error contract, and the complete set of codes behind it.
 *
 * A code is a stable string, never a number, and it never carries personal data. It is the only
 * thing the server promises: the client localizes from the code, so a message may be rewritten in
 * either language without breaking a single consumer, and an integration may switch on the code
 * without parsing prose.
 *
 * Three properties are load-bearing and each is pinned by a test.
 *
 * - **Every code names its domain in its own prefix.** `PERSON_NOT_FOUND` is in the `person`
 *   domain and nothing else can be, so a reader never has to consult the table to know where a
 *   code came from and a reviewer can see a mis-filed code without running anything.
 * - **Every code has exactly one HTTP status.** A code that could arrive as either a 404 or a 409
 *   is two codes wearing one name, and a client cannot branch on it.
 * - **Every code has a message key in both catalogues.** The parity gate refuses a code whose
 *   translation is missing in either language, which is the failure this taxonomy exists to make
 *   impossible: an untranslated error is the one a user meets on their worst day.
 *
 * No message a code resolves to ever interpolates a name, a document number, a telephone number,
 * an email address or a pass code. The permitted placeholders are enumerated in
 * `../i18n/keys.ts` and enforced there, because "we were careful" is not a control.
 */
/** The domains of Section 1's error taxonomy, in the order they are grouped below. */
export const ERROR_DOMAINS = [
    'auth',
    'authz',
    'validation',
    'person',
    'credential',
    'presence',
    'dining',
    'field',
    'backup',
    'export',
    'alert',
    'api-key',
    'system',
];
/**
 * The complete taxonomy.
 *
 * Grouped by domain and, inside a domain, ordered so that the shape of the group is readable:
 * identity and lookup first, then state conflicts, then limits. Adding a code here without adding
 * its translation to both catalogues turns `test:i18n` red.
 */
export const ERROR_CODES = {
    // -----------------------------------------------------------------------------------------------
    // auth: who is asking, and whether they proved it. Also the `users` table, which the auth domain
    // owns: there is no separate `user` domain, because every failure it can produce is an
    // authentication-surface failure.
    // -----------------------------------------------------------------------------------------------
    AUTH_CREDENTIALS_REQUIRED: {
        domain: 'auth',
        status: 401,
        messageKey: 'errors.auth.credentials_required',
    },
    AUTH_INVALID_CREDENTIALS: {
        domain: 'auth',
        status: 401,
        messageKey: 'errors.auth.invalid_credentials',
    },
    AUTH_ACCOUNT_LOCKED: { domain: 'auth', status: 423, messageKey: 'errors.auth.account_locked' },
    AUTH_ACCOUNT_DISABLED: {
        domain: 'auth',
        status: 403,
        messageKey: 'errors.auth.account_disabled',
    },
    AUTH_SESSION_EXPIRED: { domain: 'auth', status: 401, messageKey: 'errors.auth.session_expired' },
    AUTH_SESSION_IDLE_LOCKED: {
        domain: 'auth',
        status: 401,
        messageKey: 'errors.auth.session_idle_locked',
    },
    AUTH_PASSWORD_CHANGE_REQUIRED: {
        domain: 'auth',
        status: 403,
        messageKey: 'errors.auth.password_change_required',
    },
    AUTH_PASSWORD_TOO_WEAK: {
        domain: 'auth',
        status: 400,
        messageKey: 'errors.auth.password_too_weak',
    },
    AUTH_PASSWORD_REUSED: { domain: 'auth', status: 400, messageKey: 'errors.auth.password_reused' },
    AUTH_TOTP_REQUIRED: { domain: 'auth', status: 403, messageKey: 'errors.auth.totp_required' },
    AUTH_TOTP_INVALID: { domain: 'auth', status: 401, messageKey: 'errors.auth.totp_invalid' },
    AUTH_TOTP_ALREADY_ENROLLED: {
        domain: 'auth',
        status: 409,
        messageKey: 'errors.auth.totp_already_enrolled',
    },
    AUTH_RECOVERY_CODE_INVALID: {
        domain: 'auth',
        status: 401,
        messageKey: 'errors.auth.recovery_code_invalid',
    },
    AUTH_PIN_NOT_SET: { domain: 'auth', status: 403, messageKey: 'errors.auth.pin_not_set' },
    AUTH_PIN_INVALID: { domain: 'auth', status: 401, messageKey: 'errors.auth.pin_invalid' },
    AUTH_PIN_LOCKED: { domain: 'auth', status: 423, messageKey: 'errors.auth.pin_locked' },
    AUTH_CSRF_TOKEN_INVALID: {
        domain: 'auth',
        status: 403,
        messageKey: 'errors.auth.csrf_token_invalid',
    },
    AUTH_ORIGIN_REJECTED: { domain: 'auth', status: 403, messageKey: 'errors.auth.origin_rejected' },
    AUTH_USER_NOT_FOUND: { domain: 'auth', status: 404, messageKey: 'errors.auth.user_not_found' },
    AUTH_EMAIL_TAKEN: { domain: 'auth', status: 409, messageKey: 'errors.auth.email_taken' },
    AUTH_USER_CONFLICT: { domain: 'auth', status: 409, messageKey: 'errors.auth.user_conflict' },
    // Section 1.7's self-service reset, which exists only where the installation has a mail server.
    // Three codes and not four: "this installation cannot do that" is `SYSTEM_FEATURE_DISABLED`,
    // because the browser hides the control when the capability is off and a hand-made request is the
    // only way to reach the refusal, so a sentence of its own would buy nothing and cost two more
    // catalogue messages in a bundle that is measured in bytes.
    AUTH_RESET_CHALLENGE_MISSING: {
        domain: 'auth',
        status: 400,
        messageKey: 'errors.auth.reset_challenge_missing',
    },
    // **One code for five states**, deliberately: wrong, expired, already spent, out of attempts, or
    // minted for the other purpose. Each of those is a fact about somebody else's account, and a
    // caller who holds the code learns nothing from the distinction that they could not already see.
    AUTH_RESET_CODE_INVALID: {
        domain: 'auth',
        status: 401,
        messageKey: 'errors.auth.reset_code_invalid',
    },
    // The escalation bar: a password that was itself restored by e-mail cannot then be used to clear
    // the authenticator, or possession of a mailbox would be possession of the account.
    AUTH_RESET_NOT_PERMITTED: {
        domain: 'auth',
        status: 409,
        messageKey: 'errors.auth.reset_not_permitted',
    },
    // The same bar, applied to the ADMINISTRATIVE path. Clearing an authenticator on an account whose
    // password was restored by e-mail would hand that restored password sole control of the account:
    // an attacker who reads a mailbox restores the password, is stopped by the second factor, and
    // then waits for the owner to ask an administrator to clear the very thing stopping them. The
    // password is reset first, which replaces it with one the attacker does not have.
    AUTH_RESET_PASSWORD_UNTRUSTED: {
        domain: 'auth',
        status: 409,
        messageKey: 'errors.auth.reset_password_untrusted',
    },
    // -----------------------------------------------------------------------------------------------
    // authz: AD-36, checked at the route and again at the object. OWASP A01 is the top risk here, so
    // the two checks have distinct codes: a route-level refusal and an object-level refusal look the
    // same to a caller but mean very different things in an audit trail.
    // -----------------------------------------------------------------------------------------------
    AUTHZ_ROLE_FORBIDDEN: { domain: 'authz', status: 403, messageKey: 'errors.authz.role_forbidden' },
    AUTHZ_OBJECT_FORBIDDEN: {
        domain: 'authz',
        status: 403,
        messageKey: 'errors.authz.object_forbidden',
    },
    AUTHZ_SCOPE_MISSING: { domain: 'authz', status: 403, messageKey: 'errors.authz.scope_missing' },
    AUTHZ_PII_NOT_PERMITTED: {
        domain: 'authz',
        status: 403,
        messageKey: 'errors.authz.pii_not_permitted',
    },
    AUTHZ_ROUTE_NOT_FOR_API_KEY: {
        domain: 'authz',
        status: 403,
        messageKey: 'errors.authz.route_not_for_api_key',
    },
    AUTHZ_SUPER_ADMIN_PROTECTED: {
        domain: 'authz',
        status: 403,
        messageKey: 'errors.authz.super_admin_protected',
    },
    AUTHZ_SELF_ACTION_FORBIDDEN: {
        domain: 'authz',
        status: 403,
        messageKey: 'errors.authz.self_action_forbidden',
    },
    /**
     * The rank rule on the role being ASSIGNED, which is separate from the one on the TARGET.
     *
     * `AUTHZ_OBJECT_FORBIDDEN` says "you may not administer this account"; this says "you may
     * administer it, and you may not put it in that role". An administrator promoting an operator to
     * administrator passes the first and is stopped by the second, and a reader who saw the object
     * code there would go looking for the wrong rule.
     */
    AUTHZ_ROLE_NOT_ASSIGNABLE: {
        domain: 'authz',
        status: 403,
        messageKey: 'errors.authz.role_not_assignable',
    },
    // -----------------------------------------------------------------------------------------------
    // validation: the shape of a request. `VALIDATION_FAILED` is the envelope a field-level `details`
    // array travels in; the rest are whole-request failures with nowhere to point.
    // -----------------------------------------------------------------------------------------------
    VALIDATION_FAILED: { domain: 'validation', status: 400, messageKey: 'errors.validation.failed' },
    VALIDATION_UNKNOWN_FIELD: {
        domain: 'validation',
        status: 400,
        messageKey: 'errors.validation.unknown_field',
    },
    VALIDATION_REQUIRED: {
        domain: 'validation',
        status: 400,
        messageKey: 'errors.validation.required',
    },
    VALIDATION_TOO_SHORT: {
        domain: 'validation',
        status: 400,
        messageKey: 'errors.validation.too_short',
    },
    VALIDATION_TOO_LONG: {
        domain: 'validation',
        status: 400,
        messageKey: 'errors.validation.too_long',
    },
    /**
     * The four one-sided bound refusals, and why there is no two-sided one.
     *
     * There used to be a single `VALIDATION_OUT_OF_RANGE` reading "enter a value between {{min}} and
     * {{max}}", and it could never be rendered: the validation library raises `too_small` and
     * `too_big` as SEPARATE issues, so no single issue has ever carried both bounds, and every one of
     * its sixteen uses emitted a detail whose message showed the braces to whoever read it. A bound
     * that reports one end must say one end.
     *
     * Characters and values and elements are three different things, so they are three different
     * sentences rather than one with a placeholder for the noun: `{{maxLength}}` counts characters,
     * `{{max}}` bounds a number, and `errors.validation.too_many_items` counts members of a list. The
     * issue's own `origin` is what decides which, in `errors/details.ts`, so the message can never be
     * the thing that decides what the number means.
     *
     * Like `VALIDATION_TOO_SHORT` and `VALIDATION_UNKNOWN_FIELD` before them, these are codes a
     * caller meets as a `details[].messageKey` inside a `VALIDATION_FAILED` rather than as a
     * top-level `error.code`. They are in the taxonomy because a catalogue key no code resolves to is
     * refused by `test:i18n`, and each is a legitimate top-level code the day a route refuses one
     * field on its own.
     */
    VALIDATION_BELOW_MINIMUM: {
        domain: 'validation',
        status: 400,
        messageKey: 'errors.validation.below_minimum',
    },
    VALIDATION_ABOVE_MAXIMUM: {
        domain: 'validation',
        status: 400,
        messageKey: 'errors.validation.above_maximum',
    },
    VALIDATION_TOO_FEW_ITEMS: {
        domain: 'validation',
        status: 400,
        messageKey: 'errors.validation.too_few_items',
    },
    VALIDATION_TOO_MANY_ITEMS: {
        domain: 'validation',
        status: 400,
        messageKey: 'errors.validation.too_many_items',
    },
    /**
     * A machine key that is not shaped like one.
     *
     * Separate from `VALIDATION_INVALID_FORMAT`, which every unannotated shape failure in the product
     * collapses to, because "the format is not valid" tells an administrator naming a field, or an
     * integrator naming an export column, nothing they can act on. The sentence carries the grammar
     * rather than a placeholder holding it: a regular expression in an error body is reflected schema
     * detail, and it is a string a translator could not move.
     */
    VALIDATION_INVALID_KEY_FORMAT: {
        domain: 'validation',
        status: 400,
        messageKey: 'errors.validation.invalid_key_format',
    },
    /**
     * The two ways a date range is refused, which are two different mistakes.
     *
     * Both were `VALIDATION_OUT_OF_RANGE` and neither is a bound on a value: one is a period wider
     * than the cap a panel will answer, and the other is an end before its own start. Telling an
     * administrator to "enter a value between {{min}} and {{max}}" for either is wrong twice over,
     * and the braces stood because a refinement carries no bound for anything to fill them from.
     */
    VALIDATION_RANGE_TOO_WIDE: {
        domain: 'validation',
        status: 400,
        messageKey: 'errors.validation.range_too_wide',
    },
    VALIDATION_RANGE_INVERTED: {
        domain: 'validation',
        status: 400,
        messageKey: 'errors.validation.range_inverted',
    },
    /**
     * The line a detail falls back to when nothing more specific can be completed.
     *
     * Separate from `VALIDATION_FAILED`, which is the ENVELOPE, and the difference is number
     * agreement: a detail renders as "Nacionalidad: <message>", so borrowing the envelope's "some of
     * the information is not valid" put a plural predicate against a single control and repeated the
     * banner a line above it word for word. A detail-only code, like the four bounds above.
     */
    VALIDATION_UNSPECIFIED: {
        domain: 'validation',
        status: 400,
        messageKey: 'errors.validation.unspecified',
    },
    VALIDATION_INVALID_FORMAT: {
        domain: 'validation',
        status: 400,
        messageKey: 'errors.validation.invalid_format',
    },
    VALIDATION_INVALID_OPTION: {
        domain: 'validation',
        status: 400,
        messageKey: 'errors.validation.invalid_option',
    },
    VALIDATION_INVALID_DATE: {
        domain: 'validation',
        status: 400,
        messageKey: 'errors.validation.invalid_date',
    },
    VALIDATION_DATE_IN_FUTURE: {
        domain: 'validation',
        status: 400,
        messageKey: 'errors.validation.date_in_future',
    },
    VALIDATION_DATE_TOO_OLD: {
        domain: 'validation',
        status: 400,
        messageKey: 'errors.validation.date_too_old',
    },
    VALIDATION_EXPIRY_BEFORE_BIRTH: {
        domain: 'validation',
        status: 400,
        messageKey: 'errors.validation.expiry_before_birth',
    },
    VALIDATION_INVALID_PHONE: {
        domain: 'validation',
        status: 400,
        messageKey: 'errors.validation.invalid_phone',
    },
    VALIDATION_INVALID_EMAIL: {
        domain: 'validation',
        status: 400,
        messageKey: 'errors.validation.invalid_email',
    },
    VALIDATION_INVALID_COUNTRY: {
        domain: 'validation',
        status: 400,
        messageKey: 'errors.validation.invalid_country',
    },
    VALIDATION_INVALID_PASSPORT: {
        domain: 'validation',
        status: 400,
        messageKey: 'errors.validation.invalid_passport',
    },
    VALIDATION_CURSOR_INVALID: {
        domain: 'validation',
        status: 400,
        messageKey: 'errors.validation.cursor_invalid',
    },
    VALIDATION_CURSOR_FILTER_MISMATCH: {
        domain: 'validation',
        status: 400,
        messageKey: 'errors.validation.cursor_filter_mismatch',
    },
    VALIDATION_IDEMPOTENCY_KEY_REQUIRED: {
        domain: 'validation',
        status: 400,
        messageKey: 'errors.validation.idempotency_key_required',
    },
    /**
     * A key that is present and is not a uuid, which is not the same as one that is missing.
     *
     * `VALIDATION_IDEMPOTENCY_KEY_REQUIRED` says to send one; this says the one that was sent is the
     * wrong shape, and an integrator reading "the format is not valid" against a header they did send
     * has to guess which of the two it is.
     */
    VALIDATION_IDEMPOTENCY_KEY_INVALID: {
        domain: 'validation',
        status: 400,
        messageKey: 'errors.validation.idempotency_key_invalid',
    },
    VALIDATION_IDEMPOTENCY_KEY_REUSED: {
        domain: 'validation',
        status: 409,
        messageKey: 'errors.validation.idempotency_key_reused',
    },
    VALIDATION_PAYLOAD_TOO_LARGE: {
        domain: 'validation',
        status: 413,
        messageKey: 'errors.validation.payload_too_large',
    },
    VALIDATION_UNSUPPORTED_MEDIA_TYPE: {
        domain: 'validation',
        status: 415,
        messageKey: 'errors.validation.unsupported_media_type',
    },
    /**
     * The six ways an uploaded spreadsheet cannot be read at all.
     *
     * Each is met as a `details[].messageKey` on the `body.file` path inside a `VALIDATION_FAILED`,
     * like `VALIDATION_BELOW_MINIMUM`, and each says what to DO: save it as .xlsx, remove the
     * password, re-save it, split it, add the header row, add the people. They are separate codes
     * rather than one with a reason placeholder because a placeholder would have to carry a
     * sentence, and a catalogue key no code resolves to is refused by `test:i18n`.
     */
    VALIDATION_SPREADSHEET_NOT_XLSX: {
        domain: 'validation',
        status: 400,
        messageKey: 'errors.validation.spreadsheet_not_xlsx',
    },
    VALIDATION_SPREADSHEET_ENCRYPTED: {
        domain: 'validation',
        status: 400,
        messageKey: 'errors.validation.spreadsheet_encrypted',
    },
    VALIDATION_SPREADSHEET_CORRUPT: {
        domain: 'validation',
        status: 400,
        messageKey: 'errors.validation.spreadsheet_corrupt',
    },
    VALIDATION_SPREADSHEET_TOO_LARGE: {
        domain: 'validation',
        status: 400,
        messageKey: 'errors.validation.spreadsheet_too_large',
    },
    VALIDATION_SPREADSHEET_NO_HEADER: {
        domain: 'validation',
        status: 400,
        messageKey: 'errors.validation.spreadsheet_no_header',
    },
    VALIDATION_SPREADSHEET_NO_ROWS: {
        domain: 'validation',
        status: 400,
        messageKey: 'errors.validation.spreadsheet_no_rows',
    },
    // -----------------------------------------------------------------------------------------------
    // person: the record itself. `PERSON_SHREDDED` is 410 rather than 404 on purpose: after AD-10 the
    // row is still there and the operator is entitled to know the erasure happened, which is what the
    // shred ledger exists to prove.
    // -----------------------------------------------------------------------------------------------
    PERSON_NOT_FOUND: { domain: 'person', status: 404, messageKey: 'errors.person.not_found' },
    PERSON_SHREDDED: { domain: 'person', status: 410, messageKey: 'errors.person.shredded' },
    PERSON_VERSION_CONFLICT: {
        domain: 'person',
        status: 409,
        messageKey: 'errors.person.version_conflict',
    },
    PERSON_ARCHIVED: { domain: 'person', status: 409, messageKey: 'errors.person.archived' },
    PERSON_BLOCKED: { domain: 'person', status: 409, messageKey: 'errors.person.blocked' },
    PERSON_CONSENT_REQUIRED: {
        domain: 'person',
        status: 400,
        messageKey: 'errors.person.consent_required',
    },
    PERSON_DUPLICATE_UNIQUE_VALUE: {
        domain: 'person',
        status: 409,
        messageKey: 'errors.person.duplicate_unique_value',
    },
    /**
     * A registration that carries no band must carry a reference code instead.
     *
     * **This is the guard of `CREDENTIAL_CODE_REQUIRED` replaced rather than removed.** Under
     * `passes.no_band_at_registration = 'defer'` a person may be registered with no pass, because the
     * band is bound later from their own record — but a record with neither a pass nor a reference is
     * findable only by name, which at a facility where families share surnames is the duplicate
     * cascade AD-22 and AD-23 exist to prevent. So the refusal moves rather than disappearing: what
     * cannot happen is somebody walking away from the desk with nothing that identifies their record.
     *
     * Raised before anything is written, so nothing is rolled back, and only for a session actor: an
     * API key registers people and never obtains a pass by design (AD-34), and demanding a reference
     * of it would break the pre-registration that carve-out exists for.
     *
     * It names the field in a `details` entry rather than interpolating it, because the sentence has
     * no placeholder to fill — and an unfilled placeholder in this product is rendered literally.
     */
    PERSON_REFERENCE_REQUIRED: {
        domain: 'person',
        status: 400,
        messageKey: 'errors.person.reference_required',
    },
    PERSON_IMAGE_NOT_FOUND: {
        domain: 'person',
        status: 404,
        messageKey: 'errors.person.image_not_found',
    },
    /**
     * The photograph could not be read at all: no bytes, no recognisable signature, an unreadable
     * header, or a decode that failed after everything else passed.
     *
     * **It keeps only the reasons whose remedy really is the same sentence**, and the five codes below
     * carry the ones whose remedy is not. `images/validate.ts` distinguishes eleven reasons and used
     * to collapse every one of them into this code on the argument that an operator's next act is
     * always "send a different photograph". That argument does not survive the desk: converting an
     * iPhone HEIC, choosing a smaller file and taking the picture again are three different acts, and
     * the operator manual had to crowd the HEIC advice into this row's "what to do" column because the
     * product would not say it. The map from reason to code is total over the union, so a twelfth
     * reason is a compile error rather than a silent fall back to this one.
     */
    PERSON_IMAGE_REJECTED: {
        domain: 'person',
        status: 400,
        messageKey: 'errors.person.image_rejected',
    },
    /** A real image in a format this build does not store. The accepted four are in the sentence. */
    PERSON_IMAGE_FORMAT_UNSUPPORTED: {
        domain: 'person',
        status: 400,
        messageKey: 'errors.person.image_format_unsupported',
    },
    /**
     * HEIC or HEIF, which is what a phone camera writes by default and therefore the commonest refusal
     * at a desk. Its own code because the remedy is specific and nothing else in the product names it:
     * this build's image library decodes AVIF and not the HEVC coding inside a HEIF container.
     */
    PERSON_IMAGE_HEIC_UNSUPPORTED: {
        domain: 'person',
        status: 400,
        messageKey: 'errors.person.image_heic_unsupported',
    },
    /** More than one frame. Orientation cannot be applied to one, and an identity photograph is one. */
    PERSON_IMAGE_ANIMATED: {
        domain: 'person',
        status: 400,
        messageKey: 'errors.person.image_animated',
    },
    /**
     * Too many pixels, or an edge past the ceiling. One code for both, because "make it smaller" is
     * one remedy and the operator cannot act on which of the two bounds it crossed.
     */
    PERSON_IMAGE_RESOLUTION_TOO_HIGH: {
        domain: 'person',
        status: 400,
        messageKey: 'errors.person.image_resolution_too_high',
    },
    /** Below the shortest edge a face is recognisable at, which is the opposite mistake. */
    PERSON_IMAGE_TOO_SMALL: {
        domain: 'person',
        status: 400,
        messageKey: 'errors.person.image_too_small',
    },
    PERSON_IMAGE_LIMIT_REACHED: {
        domain: 'person',
        status: 409,
        messageKey: 'errors.person.image_limit_reached',
    },
    // Its own code rather than borrowing `PERSON_DUPLICATE_UNIQUE_VALUE`, which names the field it is
    // about. An ad-hoc field's name is prose an operator invented, and interpolating it into a message
    // written for machine keys would put a person's own words into an error body and a log line — the
    // one thing `PERMITTED_PLACEHOLDERS` exists to stop. So this sentence names nothing.
    PERSON_ADHOC_NAME_DUPLICATE: {
        domain: 'person',
        status: 400,
        messageKey: 'errors.person.adhoc_name_duplicate',
    },
    PERSON_ADHOC_FIELD_LIMIT_REACHED: {
        domain: 'person',
        status: 409,
        messageKey: 'errors.person.adhoc_field_limit_reached',
    },
    PERSON_REVISION_NOT_FOUND: {
        domain: 'person',
        status: 404,
        messageKey: 'errors.person.revision_not_found',
    },
    PERSON_SHRED_CONFIRMATION_INVALID: {
        domain: 'person',
        status: 400,
        messageKey: 'errors.person.shred_confirmation_invalid',
    },
    // **One code for two operations, deliberately.** Archiving and erasing meet the same condition,
    // this record is recorded inside the facility, and have the same remedy, record the exit first.
    // The precedent for splitting is `FIELD_VERSION_CONFLICT`, and its stated ground is "the same
    // mechanism and a completely different sentence to read", which does not hold here: two codes
    // would force two catalogue sentences, in two languages, that must be kept saying one rule, and
    // `code.message-key-shared` forbids them sharing one key. The caller knows which operation it
    // attempted, because it made the request.
    PERSON_INSIDE_BUILDING: {
        domain: 'person',
        status: 409,
        messageKey: 'errors.person.inside_building',
    },
    // -----------------------------------------------------------------------------------------------
    // credential: the pass. A malformed code and an unknown code are separate codes because the first
    // never reached the database and the second did, and only the second is a security signal.
    // -----------------------------------------------------------------------------------------------
    CREDENTIAL_NOT_FOUND: {
        domain: 'credential',
        status: 404,
        messageKey: 'errors.credential.not_found',
    },
    CREDENTIAL_CODE_MALFORMED: {
        domain: 'credential',
        status: 400,
        messageKey: 'errors.credential.code_malformed',
    },
    CREDENTIAL_CODE_UNKNOWN: {
        domain: 'credential',
        status: 404,
        messageKey: 'errors.credential.code_unknown',
    },
    CREDENTIAL_REVOKED: {
        domain: 'credential',
        status: 409,
        messageKey: 'errors.credential.revoked',
    },
    CREDENTIAL_SUPERSEDED: {
        domain: 'credential',
        status: 409,
        messageKey: 'errors.credential.superseded',
    },
    CREDENTIAL_ALREADY_ACTIVE: {
        domain: 'credential',
        status: 409,
        messageKey: 'errors.credential.already_active',
    },
    CREDENTIAL_TYPE_DISABLED: {
        domain: 'credential',
        status: 400,
        messageKey: 'errors.credential.type_disabled',
    },
    /**
     * AD-59: the installation issues pre-printed bands, and this request carried no band.
     *
     * A refusal rather than a silent mint, because the two modes are two registration procedures and
     * minting under the pre-printed one produces a credential with no physical token: the person
     * walks away with nothing on their wrist and the desk finds out at the gate. The break-glass
     * path is `passes.allow_emergency_mint`, which is a deliberate act with its own `origin`.
     */
    CREDENTIAL_CODE_REQUIRED: {
        domain: 'credential',
        status: 400,
        messageKey: 'errors.credential.code_required',
    },
    /**
     * AD-60: the installation mints its own codes, so a caller-supplied one is refused.
     *
     * This is the invariant `issueCredentialRequestSchema` states in writing — nobody may CHOOSE
     * their own code. In pre-printed mode a submitted code is legitimate because it must already
     * exist as an unclaimed blank; in on-demand mode there are no blanks, so accepting one would
     * let a careless or compromised operator assign a guessable code to a real person.
     */
    CREDENTIAL_CODE_NOT_ACCEPTED: {
        domain: 'credential',
        status: 400,
        messageKey: 'errors.credential.code_not_accepted',
    },
    /**
     * AD-62: this band was already claimed, and a blank is single-use.
     *
     * **It deliberately names nothing.** The band belongs to somebody, and the whole error contract
     * is built so that no record identifier can travel in a refusal: `ErrorDetail` is
     * `{path, messageKey, params?}` where `path` is documented as never a value, and `params` is
     * keyed by the interpolation allowlist, which a `short_ref` may not join. The operator learns
     * whose band it is from the lookup route, whose SUCCESS shape carries the `short_ref` for a
     * claimed code, and which the registration form calls before it submits.
     */
    CREDENTIAL_BLANK_ALREADY_CLAIMED: {
        domain: 'credential',
        status: 409,
        messageKey: 'errors.credential.blank_already_claimed',
    },
    /**
     * The pass moved between the screen that read it and the request that acted on it.
     *
     * `revokeCredentialRequestSchema` carries a `version` for exactly this, and without a code to
     * report the mismatch the guard would have nothing to say: a second operator's reissue would be
     * silently overwritten by the first operator's revocation of a pass that no longer exists.
     */
    CREDENTIAL_VERSION_CONFLICT: {
        domain: 'credential',
        status: 409,
        messageKey: 'errors.credential.version_conflict',
    },
    /** AD-62: the band, or the whole box it came in, has been withdrawn. */
    CREDENTIAL_BLANK_VOID: {
        domain: 'credential',
        status: 409,
        messageKey: 'errors.credential.blank_void',
    },
    /**
     * The code is a band this installation imported and nobody has registered yet.
     *
     * Distinct from `CREDENTIAL_CODE_UNKNOWN` because the operator's next move is different: an
     * unknown code is not this facility's band at all, while this one is on the shelf and its holder
     * needs sending to registration. Section 1.11 makes the message mode-aware, and the resolve path
     * only reaches this outcome in pre-printed mode: under on-demand issuance there is no
     * registration step for a band, so the sentence would be false and the mode-neutral
     * not-recognised wording is used instead.
     */
    CREDENTIAL_BLANK_UNCLAIMED: {
        domain: 'credential',
        status: 404,
        messageKey: 'errors.credential.blank_unclaimed',
    },
    /**
     * AD-60: a stored pass index and the index the lookup computed were built under different
     * generations of the PASS index, so the comparison cannot be made.
     *
     * Its own code rather than reporting the band as unknown, which is the whole point: the lookup
     * is a plain equality on bytes and cannot see the mismatch by itself, so every band in the box
     * would read as "not recognised" with nothing to explain it.
     */
    CREDENTIAL_INDEX_VERSION_MISMATCH: {
        domain: 'credential',
        status: 409,
        messageKey: 'errors.credential.index_version_mismatch',
    },
    CREDENTIAL_BATCH_NOT_FOUND: {
        domain: 'credential',
        status: 404,
        messageKey: 'errors.credential.batch_not_found',
    },
    CREDENTIAL_BATCH_LABEL_TAKEN: {
        domain: 'credential',
        status: 409,
        messageKey: 'errors.credential.batch_label_taken',
    },
    /** AD-64: the import is all-or-nothing, so one code already known refuses the whole file. */
    CREDENTIAL_BATCH_CODE_COLLISION: {
        domain: 'credential',
        status: 409,
        messageKey: 'errors.credential.batch_code_collision',
    },
    /**
     * One code from the file that already exists, named per row inside a `CREDENTIAL_BATCH_CODE_COLLISION`.
     *
     * The envelope's own message counts the collisions over the whole request, which is the right
     * sentence there and meaningless against a single serial; this is what each `details[]` entry
     * says, with the serial in its `path` rather than in its prose — a serial is a number an operator
     * quotes and the code itself never travels.
     */
    CREDENTIAL_BATCH_SERIAL_IN_USE: {
        domain: 'credential',
        status: 409,
        messageKey: 'errors.credential.batch_serial_in_use',
    },
    CREDENTIAL_BATCH_FILE_INVALID: {
        domain: 'credential',
        status: 400,
        messageKey: 'errors.credential.batch_file_invalid',
    },
    /**
     * The multipart body carried no file part at all.
     *
     * Its own code rather than the bare `VALIDATION_FAILED` the other four multipart faults answer
     * with, because this is the one an integrator wiring against the published document actually hits:
     * the others are unreachable from the product's own import panel.
     */
    CREDENTIAL_BATCH_FILE_MISSING: {
        domain: 'credential',
        status: 400,
        messageKey: 'errors.credential.batch_file_missing',
    },
    CREDENTIAL_BATCH_DIGEST_MISMATCH: {
        domain: 'credential',
        status: 409,
        messageKey: 'errors.credential.batch_digest_mismatch',
    },
    CREDENTIAL_BATCH_ALREADY_VOID: {
        domain: 'credential',
        status: 409,
        messageKey: 'errors.credential.batch_already_void',
    },
    CREDENTIAL_BLANK_NOT_FOUND: {
        domain: 'credential',
        status: 404,
        messageKey: 'errors.credential.blank_not_found',
    },
    // -----------------------------------------------------------------------------------------------
    // presence: the gate. Section 1.11's outcome matrix decides which of these an operator can see.
    // -----------------------------------------------------------------------------------------------
    PRESENCE_REASON_REQUIRED: {
        domain: 'presence',
        status: 400,
        messageKey: 'errors.presence.reason_required',
    },
    PRESENCE_REASON_UNKNOWN: {
        domain: 'presence',
        status: 400,
        messageKey: 'errors.presence.reason_unknown',
    },
    PRESENCE_DIRECTION_CONFLICT: {
        domain: 'presence',
        status: 409,
        messageKey: 'errors.presence.direction_conflict',
    },
    PRESENCE_OVERRIDE_NOT_PERMITTED: {
        domain: 'presence',
        status: 403,
        messageKey: 'errors.presence.override_not_permitted',
    },
    PRESENCE_EVENT_NOT_FOUND: {
        domain: 'presence',
        status: 404,
        messageKey: 'errors.presence.event_not_found',
    },
    PRESENCE_BACKFILL_WINDOW_EXCEEDED: {
        domain: 'presence',
        status: 400,
        messageKey: 'errors.presence.backfill_window_exceeded',
    },
    PRESENCE_CHECKPOINT_UNKNOWN: {
        domain: 'presence',
        status: 400,
        messageKey: 'errors.presence.checkpoint_unknown',
    },
    // -----------------------------------------------------------------------------------------------
    // dining: the counter. One breakfast, one lunch and one dinner per person per day.
    //
    // One code, and the shortness of this list is the design rather than an omission. Everything else
    // the counter can refuse already has a word: an archived record travels as `PERSON_ARCHIVED` on a
    // recorded row, a call while the feature is off is `SYSTEM_FEATURE_DISABLED`, a reason token
    // outside the closed three is refused at the request edge by the enum naming `body.reasonCode`,
    // and a reused key is `VALIDATION_IDEMPOTENCY_KEY_REUSED`. Minting a bespoke code for any of those
    // would be a second word for one condition.
    // -----------------------------------------------------------------------------------------------
    /**
     * The allowance has already been taken and no reason arrived, so nothing was written.
     *
     * The counterpart of `PRESENCE_REASON_REQUIRED` and it behaves the same way: it is a client that
     * has not asked its operator yet rather than an outcome, so no `meal_events` row records it and
     * the key it did not consume stays spendable for the reasoned retry. The browser normally
     * pre-empts it, because the scan already told it the meal was taken; what reaches this code is the
     * race where another counter served the same person between the scan and the press.
     *
     * The message names no meal. `{{direction}}`'s precedent shows what naming a machine token inside
     * a translated sentence looks like, and the screen already knows which button was pressed.
     */
    DINING_REASON_REQUIRED: {
        domain: 'dining',
        status: 400,
        messageKey: 'errors.dining.reason_required',
    },
    // -----------------------------------------------------------------------------------------------
    // field: the admin-defined schema of AD-16 and AD-17. A retype has no code that permits it,
    // because there is no request that may perform one.
    // -----------------------------------------------------------------------------------------------
    FIELD_NOT_FOUND: { domain: 'field', status: 404, messageKey: 'errors.field.not_found' },
    FIELD_KEY_TAKEN: { domain: 'field', status: 409, messageKey: 'errors.field.key_taken' },
    FIELD_TYPE_IMMUTABLE: { domain: 'field', status: 409, messageKey: 'errors.field.type_immutable' },
    FIELD_CORE_PROTECTED: { domain: 'field', status: 409, messageKey: 'errors.field.core_protected' },
    FIELD_ALREADY_RETIRED: {
        domain: 'field',
        status: 409,
        messageKey: 'errors.field.already_retired',
    },
    FIELD_OPTIONS_REQUIRED: {
        domain: 'field',
        status: 400,
        messageKey: 'errors.field.options_required',
    },
    /**
     * Two choices of one field declaring the same stored value.
     *
     * Its own code rather than the `FIELD_KEY_TAKEN` the option list used to borrow, which reads "a
     * field with the key «{{fieldKey}}» already exists" — a sentence about a different thing, with a
     * placeholder nothing could fill, shown to an administrator who had just typed a duplicate choice.
     * It names nothing, for the reason `PERSON_ADHOC_NAME_DUPLICATE` gives above: the refusal already
     * points at the offending row through its `details[].path`.
     */
    FIELD_OPTION_VALUE_DUPLICATE: {
        domain: 'field',
        status: 400,
        messageKey: 'errors.field.option_value_duplicate',
    },
    /**
     * A choice an amendment removed that records still hold.
     *
     * The envelope counts the records; each `details[]` entry names the offending choice by its
     * POSITION in the stored option list (`options.<n>`) and carries that choice's own count. The
     * position rather than the value, deliberately: an option value names a CATEGORY OF PERSON — a
     * vulnerability field's choices are things like `unaccompanied_minor` — so interpolating one
     * beside a count would put a population statistic about this facility into a message that is
     * rendered on a shared screen and can be mailed. The browser holds the stored definition and
     * renders that choice's own bilingual label instead, which is both safer and what the
     * administrator actually reads. `options.<n>` is a third `ErrorDetail.path` namespace beside the
     * four request sections and `SYSTEM_SETTING_INVALID`'s setting keys, for the reason that one has:
     * the choice being named is one the request REMOVED, so there is no request field to point at.
     */
    FIELD_OPTION_IN_USE: { domain: 'field', status: 409, messageKey: 'errors.field.option_in_use' },
    FIELD_UNIQUE_CONFLICT: {
        domain: 'field',
        status: 409,
        messageKey: 'errors.field.unique_conflict',
    },
    // Its own code rather than borrowing `PERSON_VERSION_CONFLICT`: the two are the same mechanism and
    // a completely different sentence to read. One says another operator edited the person in front of
    // you; this one says an administrator changed the registry while your form was open, and the
    // recovery is to reload the field list rather than to compare two versions of a record.
    FIELD_VERSION_CONFLICT: {
        domain: 'field',
        status: 409,
        messageKey: 'errors.field.version_conflict',
    },
    // -----------------------------------------------------------------------------------------------
    // backup: AD-38's archive and the restore path. `BACKUP_SCHEMA_TOO_NEW` is the refusal that keeps
    // an older binary from restoring an archive it cannot read.
    // -----------------------------------------------------------------------------------------------
    BACKUP_NOT_FOUND: { domain: 'backup', status: 404, messageKey: 'errors.backup.not_found' },
    BACKUP_IN_PROGRESS: { domain: 'backup', status: 409, messageKey: 'errors.backup.in_progress' },
    BACKUP_RESTORE_IN_PROGRESS: {
        domain: 'backup',
        status: 409,
        messageKey: 'errors.backup.restore_in_progress',
    },
    BACKUP_MAINTENANCE_REQUIRED: {
        domain: 'backup',
        status: 409,
        messageKey: 'errors.backup.maintenance_required',
    },
    BACKUP_ARCHIVE_CORRUPT: {
        domain: 'backup',
        status: 400,
        messageKey: 'errors.backup.archive_corrupt',
    },
    BACKUP_MANIFEST_MISMATCH: {
        domain: 'backup',
        status: 400,
        messageKey: 'errors.backup.manifest_mismatch',
    },
    BACKUP_SCHEMA_TOO_NEW: {
        domain: 'backup',
        status: 409,
        messageKey: 'errors.backup.schema_too_new',
    },
    BACKUP_RECIPIENTS_REQUIRED: {
        domain: 'backup',
        status: 400,
        messageKey: 'errors.backup.recipients_required',
    },
    BACKUP_EMAIL_TOO_LARGE: {
        domain: 'backup',
        status: 413,
        messageKey: 'errors.backup.email_too_large',
    },
    BACKUP_INSUFFICIENT_DISK: {
        domain: 'backup',
        status: 507,
        messageKey: 'errors.backup.insufficient_disk',
    },
    /**
     * The archive was written by a binary whose AD-50 `aad` differs from this one's.
     *
     * Its own code rather than `BACKUP_ARCHIVE_CORRUPT`, because the two send an operator to
     * completely different places: a corrupt archive is a disk or a transfer to distrust, and this is
     * the right archive being opened by the wrong build. The library folds that string into the
     * header binding of every file it seals, so without this check the symptom is an authentication
     * failure that reads exactly like corruption, discovered on the first real restore.
     */
    BACKUP_FORMAT_MISMATCH: {
        domain: 'backup',
        status: 409,
        messageKey: 'errors.backup.format_mismatch',
    },
    /**
     * The archive does not describe every table this binary expects, so it would restore into a
     * half-empty database.
     *
     * Separate from `BACKUP_MANIFEST_MISMATCH`, which says the archive's CONTENT disagrees with its
     * own manifest. This one says the manifest itself is short: an archive taken during a
     * broken-privileges window, where the dump role could not read a table, is complete and
     * self-consistent and still missing people.
     */
    BACKUP_ARCHIVE_INCOMPLETE: {
        domain: 'backup',
        status: 409,
        messageKey: 'errors.backup.archive_incomplete',
    },
    /**
     * This process was not given what an archive needs: the read-only dump connection.
     *
     * A refusal rather than a silent skip, because a backup that quietly does not happen is the
     * failure this whole phase exists to prevent, and it raises `backup_failed` like any other.
     */
    BACKUP_NOT_CONFIGURED: {
        domain: 'backup',
        status: 503,
        messageKey: 'errors.backup.not_configured',
    },
    // -----------------------------------------------------------------------------------------------
    // export: CSV and the single-subject data export. Files expire, so `EXPORT_EXPIRED` is 410.
    // -----------------------------------------------------------------------------------------------
    EXPORT_NOT_FOUND: { domain: 'export', status: 404, messageKey: 'errors.export.not_found' },
    EXPORT_IN_PROGRESS: { domain: 'export', status: 409, messageKey: 'errors.export.in_progress' },
    EXPORT_EXPIRED: { domain: 'export', status: 410, messageKey: 'errors.export.expired' },
    EXPORT_NO_ROWS: { domain: 'export', status: 409, messageKey: 'errors.export.no_rows' },
    EXPORT_TOO_LARGE: { domain: 'export', status: 413, messageKey: 'errors.export.too_large' },
    /**
     * The generation failed, so there is no artifact and there never will be under this id.
     *
     * A 409 rather than a 404, because the row is there and an administrator looking at it needs to
     * be told the difference between "not yet" (`EXPORT_IN_PROGRESS`) and "this one is not coming".
     */
    EXPORT_FAILED: { domain: 'export', status: 409, messageKey: 'errors.export.failed' },
    /**
     * The one-time password did not open the artifact.
     *
     * A 403 rather than a 401, because the caller IS authenticated: what failed is the second factor
     * on this particular file. It carries no hint about whether the password was close, because the
     * check is an AES-GCM tag verification and there is nothing to be close to.
     */
    EXPORT_PASSWORD_INVALID: {
        domain: 'export',
        status: 403,
        messageKey: 'errors.export.password_invalid',
    },
    // -----------------------------------------------------------------------------------------------
    // alert: AD-47. An anomaly is a derived query and an alert is a row, so the only thing that can
    // fail here is naming a row that is not there to be acknowledged.
    //
    // There is deliberately no ALERT_ALREADY_ACKNOWLEDGED. Acknowledging is a human saying "I have
    // seen this", and a second click on the same alert has produced exactly the state the first one
    // asked for; a distinct refusal would make the panel's ordinary double-click an error, and it
    // would tell a caller which of "unknown", "already acknowledged" and "already resolved" it hit,
    // which is three facts where one suffices.
    // -----------------------------------------------------------------------------------------------
    ALERT_NOT_FOUND: { domain: 'alert', status: 404, messageKey: 'errors.alert.not_found' },
    // -----------------------------------------------------------------------------------------------
    // api-key: AD-34. The secret is shown once and never again, which is a refusal rather than an
    // absence, so it has its own code.
    // -----------------------------------------------------------------------------------------------
    API_KEY_NOT_FOUND: { domain: 'api-key', status: 404, messageKey: 'errors.api-key.not_found' },
    API_KEY_INVALID: { domain: 'api-key', status: 401, messageKey: 'errors.api-key.invalid' },
    API_KEY_EXPIRED: { domain: 'api-key', status: 401, messageKey: 'errors.api-key.expired' },
    API_KEY_REVOKED: { domain: 'api-key', status: 401, messageKey: 'errors.api-key.revoked' },
    API_KEY_NAME_TAKEN: { domain: 'api-key', status: 409, messageKey: 'errors.api-key.name_taken' },
    API_KEY_SCOPE_UNKNOWN: {
        domain: 'api-key',
        status: 400,
        messageKey: 'errors.api-key.scope_unknown',
    },
    API_KEY_SECRET_NOT_RETRIEVABLE: {
        domain: 'api-key',
        status: 409,
        messageKey: 'errors.api-key.secret_not_retrievable',
    },
    API_KEY_RATE_LIMITED: {
        domain: 'api-key',
        status: 429,
        messageKey: 'errors.api-key.rate_limited',
    },
    // -----------------------------------------------------------------------------------------------
    // system: everything that is about the installation rather than about the request. Every one of
    // these is masked: no cause, no stack, no internal identifier (OWASP A10).
    // -----------------------------------------------------------------------------------------------
    SYSTEM_INTERNAL_ERROR: {
        domain: 'system',
        status: 500,
        messageKey: 'errors.system.internal_error',
    },
    SYSTEM_ROUTE_NOT_FOUND: {
        domain: 'system',
        status: 404,
        messageKey: 'errors.system.route_not_found',
    },
    SYSTEM_METHOD_NOT_ALLOWED: {
        domain: 'system',
        status: 405,
        messageKey: 'errors.system.method_not_allowed',
    },
    SYSTEM_REQUEST_TIMEOUT: {
        domain: 'system',
        status: 408,
        messageKey: 'errors.system.request_timeout',
    },
    SYSTEM_RATE_LIMITED: { domain: 'system', status: 429, messageKey: 'errors.system.rate_limited' },
    SYSTEM_MAINTENANCE: { domain: 'system', status: 503, messageKey: 'errors.system.maintenance' },
    SYSTEM_SEARCH_INDEX_BUILDING: {
        domain: 'system',
        status: 503,
        messageKey: 'errors.system.search_index_building',
    },
    SYSTEM_STORAGE_BUSY: { domain: 'system', status: 503, messageKey: 'errors.system.storage_busy' },
    SYSTEM_DEPENDENCY_UNAVAILABLE: {
        domain: 'system',
        status: 503,
        messageKey: 'errors.system.dependency_unavailable',
    },
    SYSTEM_KEYRING_UNAVAILABLE: {
        domain: 'system',
        status: 503,
        messageKey: 'errors.system.keyring_unavailable',
    },
    SYSTEM_AUDIT_WRITE_FAILED: {
        domain: 'system',
        status: 500,
        messageKey: 'errors.system.audit_write_failed',
    },
    SYSTEM_SETTING_NOT_FOUND: {
        domain: 'system',
        status: 404,
        messageKey: 'errors.system.setting_not_found',
    },
    SYSTEM_SETTING_INVALID: {
        domain: 'system',
        status: 400,
        messageKey: 'errors.system.setting_invalid',
    },
    SYSTEM_FEATURE_DISABLED: {
        domain: 'system',
        status: 403,
        messageKey: 'errors.system.feature_disabled',
    },
    SYSTEM_UNSUPPORTED_LOCALE: {
        domain: 'system',
        status: 400,
        messageKey: 'errors.system.unsupported_locale',
    },
};
/** Every code, as an array, for exhaustive iteration in tests, documents and generated matrices. */
export const ERROR_CODE_LIST = Object.keys(ERROR_CODES);
/** The specification for one code. Total over `ErrorCode`, so there is no undefined branch. */
export function specFor(code) {
    return ERROR_CODES[code];
}
/** The HTTP status a code is always returned with. */
export function statusFor(code) {
    return ERROR_CODES[code].status;
}
/** Narrows an arbitrary string to a known code, for parsing a response on the client. */
export function isErrorCode(value) {
    return Object.hasOwn(ERROR_CODES, value);
}
//# sourceMappingURL=codes.js.map